import React, { MouseEventHandler } from 'react';
import { ActionButtonDO } from 'src/components/visionx/buttons/ActionButton.tsx';
import { ProgressDO, ResponseErrorDO } from 'src/components/visionx/integration-status/types.ts';

export enum FormMode {
  VIEW,
  EDIT,
  NEW,
  ACTION_DIALOG,
}

export interface ChildReference {
  name: string;
  displayOrder: number;
  doRefresh?: () => void;
  changeMode?: (mode: FormMode) => void;
  doValidation?: () => boolean;
  register: (childReference: ChildReference) => void;
}

export interface FieldProperties {
  formMode: FormMode;
  fieldConfig: FieldConfigDTO;
  menuItem: IMenuDTO | undefined;
  isTableView?: boolean;
  // Dynamic data from backend — shape varies per field type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any;
  isNumber?: boolean;
  isFirstField?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rowData?: any;
  onSubmitted: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (name: string, value: any) => void;
  reference: ChildReference;
}

export interface ISystemDTO {
  menuGroups: IMenuDTO[];
  forms?: FormDTO[];
  toolbarMenus?: IMenuDTO[];
  actions?: ActionConfigDTO[];
}

export interface IMenu {
  code: string;
  label: string;
  icon: string;
  route: string;
}

export interface IMenuDTO {
  label: string;
  alwaysOpen?: boolean;
  displayOrder: number;
  code?: string;
  menuItems?: IMenuDTO[];
  help?: string;
  selectUrl?: string;
  menuType?: MenuType;
  entityGroup?: EntityGroupConfigDTO;
  singleForm?: SingleFormDTO;
  singlePage?: SinglePageDTO;
  action?: ActionConfigDTO;
  columnBasedLayout?: boolean;
  withTimestamp?: boolean;
  refreshRate?: number;
  showInSideBar?: boolean;
  tabGroup?: string;
  disabled?: boolean;
  icon?: string;
  iconOnly?: boolean;
  badge?: string;
  to?: string;
  route?: string;
  align?: string;
  url?: string;
  target?: string;
  badgeStyleClass?: string;
  class?: string;
  command?: string;
  permission?: string;
}

export enum MenuType {
  ENTITY_GROUP = 'ENTITY_GROUP',
  SINGLE_PAGE = 'SINGLE_PAGE',
  DASHBOARD = 'DASHBOARD',
  ACTION_ITEM = 'ACTION_ITEM',
}
export interface FormDTO {
  code: string;
  title: string;
  updateUrl: string;
  rowConfig: FormRowConfigDTO;
}

export interface MaintainConfigDTO {
  rows: FormRowConfigDTO[];
  restActions?: ActionConfigDTO[];
  overallRestActions?: ActionConfigDTO[];
  selectUrl: string;
}

export enum UiValidatorType {
  NOT_NULL = 'NOT_NULL',
  MIN = 'MIN',
  MAX = 'MAX',
  MIN_LENGTH = 'MIN_LENGTH',
}

export interface UiValidator {
  type: UiValidatorType;
  longValue?: number;
}

export enum ActionType {
  VIEW_ONLY = 'VIEW_ONLY',
  VIEW_EDIT = 'VIEW_EDIT',
  ENTRY = 'ENTRY',
  MFA_STATUS = 'MFA_STATUS',
}

export interface ActionConfigBaseDTO {
  buttonLabel: string;
  popUpTitle: string;
  popUpDescription: string;
  jsEditCondition: string;
  successMessage: string;
  rows: FormRowConfigDTO[];
  confirmationFormRows: FormRowConfigDTO[];
  minModalWidth: string;
  minModalHeight: string;
  parentMenuLabel: string;
  jsWarningCondition: string;
  warningMessage: string;
  confirmationMessage: string;
  confirmationUrl: string;
  confirmationFields: string[];
  downloadAction: boolean;
  multipartForm: boolean;
  dynamicResponse: boolean;
  displaySummary: boolean;
  templateUrl: string;
  icon: string;
  inLine: boolean;
  selectUrl: string;
  selectMethod: string;
  routerLink: string;
  modalCode: string;
  actionType: ActionType;
  cancelButton: string;
  cancelIcon: string;
  showCancel: boolean;
  saveButton: string;
  saveIcon: string;
  actions: ActionConfigDTO[];
  command?: MouseEventHandler<HTMLButtonElement>;
  label?: string;
}

export interface ActionConfigDTO extends ActionConfigBaseDTO {
  method: string;
  url: string;
  children: ActionConfigDTO[];
  id?: string;
  action?: ActionButtonDO['action'];
}

export interface FormColumnConfigDTO {
  fields: FieldConfigDTO[];
  code?: string;
  title?: string;
  css?: string;
  ignorePanel?: boolean;
  updateUrl?: string;
  fullWidth: boolean;
  nested?: boolean;
  jsEditCondition?: string;
  actions?: ActionConfigDTO[];
  fxFlex: string;
  multipartForm?: boolean;
  hasTitle?: boolean;
  columns?: number;
}

export interface FormRowConfigDTO {
  columns: FormColumnConfigDTO[];
  css?: string;
}

export interface SingleFormDTO {
  rows: FormRowConfigDTO[];
  title: string;
  updateUrl: EndpointDTO;
  jsEditCondition: string;
  multipartForm: boolean;
  noLabel: boolean;
  cancelButton: string;
  cancelIcon: string;
  showCancel: boolean;
  saveButton: string;
  saveIcon: string;
  templateUrl: string;
  actions: ActionConfigDTO[];
}

export interface SinglePageDTO {
  rows: FormRowConfigDTO[];
  title: string;
  selectUrl: EndpointDTO;
  properties: Record<string, unknown>;
}

export interface EndpointDTO {
  url: string;
  method: string;
  fields?: string[];
}

export interface FormPanels {
  panels: FormColumnConfigDTO[];
}

export interface FieldConfigDTO {
  name: string;
  label: string;
  type: string;
  filterApplyFor?: string[];
  externalLink?: string;
  reorderWeightField?: string;
  filterElasticName?: string;
  elasticSearchOperand?: QueryOperand;
  sortableField?: string;
  sortable?: boolean;
  canAdd?: string;
  formFullWidth?: boolean;
  displayInTable?: boolean;
  displayWhenSelected?: boolean;
  filterInTable?: boolean;
  advancedFilter?: boolean;
  addIgnore?: boolean;
  editHide?: boolean;
  editReadOnly?: string;
  addReadOnly?: boolean;
  placeholder?: string;
  validators?: UiValidator[];
  cssClassPrefix?: string;
  options?: string[];
  child?: unknown;
  noLabel?: boolean;
  lookup?: string;
  moneyFormat?: string;
  category?: string;
  labelCss?: string;
  entryCss?: string;
  tableCss?: string;
  lookupArg?: string;
  propertyName?: string;
  dateFormat?: string;
  selected?: (value: unknown) => void;
  drawOptions?: unknown[];
  decimalPlaces?: number;
  lookupData?: unknown[];
  lookupFilterable?: boolean;
  fixedCurrency?: boolean;
  suffix?: string;
  timezone?: string;
  selectUrl?: string;
  value?: unknown;
  alignRight?: boolean;
  idFieldName?: string;
  ignoreIfNotPresent?: boolean;
  popupUrl?: string;
  localeFormatted?: boolean;
  align?: string;
  jsValue?: string;
  jsRecalculate?: string;
  viewHide?: boolean;
  lookupLayout?: string;
  canReorder?: boolean;
  codeMode?: string;
  theme?: string;
  lookupInsertUrl?: string;
  newTableElementUrl?: string;
  strippedRows?: boolean;
  expandablePanel?: string;
  hideIfEmpty?: boolean;
  editIfTicked?: boolean;
  clipboard?: boolean;
  properties?: string;
  panelOptionsUrl?: string;
  lookupWatcherFieldName?: string;
  lookupWatcherFieldObjectId?: string;
  help?: string;
  objectKey?: boolean;
  clearableImgLookup?: boolean;
  sessionUserEmailFilter?: boolean;
  lookupCollection?: string;
  canClear?: boolean;
  showClipboard?: boolean;
}

export interface DataSourceDefDTO {
  type: MenuType;
  definition: MaintainConfigDTO;
}

export interface EntityGroupConfigDTO {
  titleTag: string;
  menuItems: IMenuDTO[];
  title: string;
  heko: string;
  selectUrl: string;
  createConfig: IMenuDTO;
  dataSources: DataSourceDefDTO[];
  currentMode?: number;
}

export interface PageResponseDTO {
  offset: number;
  perPage: number;
  content: TableRowDTO[];
  total: number;
  queryParams: QueryParamsDTO;
}

export enum QueryOperand {
  EQUALS = 'EQUALS',
  QUERY_STRING = 'QUERY_STRING',
  DATE_RANGE = 'DATE_RANGE',
}

export interface GetResponseDTO<T> {
  data: T;
}

export interface GuslError {
  message: string;
  messageKey: string;
  parameters: string[];
}

export interface GuslErrors {
  errors: GuslError[];
}

export interface MenubarItemDTO {
  label: string;
  icon: string;
  items?: MenubarItemDTO[];
  command?: (event: React.SyntheticEvent) => void;
}

export interface QueryParamsDTO {
  skip: number;
  limit: number;
  orderBys?: OrderByDTO[];
  rangeQuery?: RangeQueryDTO;
  rangeQueries?: RangeQueryDTO[];
  musts?: MatchQueryDTO[];
  mustNoSearch?: MatchQueryDTO[];
  mustNots?: MatchQueryDTO[];
  should?: MatchQueryDTO[];
  ins?: InQueryDTO[];
  firstAndLast?: boolean;
  total?: number;
  queryRefresher?: string | number;
}

export interface AiQueryParamsDTO {
  queryParams: Partial<QueryParamsDTO>;
  searchString: string;
  pageName: string;
  aiSearch?: boolean;
}

export interface OrderByDTO {
  field: string;
  order: string;
}

export interface RangeQueryDTO {
  field: string;
  from: string;
  to?: string;
  gt?: string;
  lt?: string;
}

export interface MatchQueryDTO {
  field: string;
  value: string | boolean;
}

export interface InQueryDTO {
  field: string;
  values: string[];
}

export interface TableRowDTO {
  id: string;
  actions?: ActionConfigDTO[];
  formMode?: FormMode;
  currentAction?: ActionConfigDTO;
  username?: string;
  code: string;
  [key: string]: unknown;
}

export interface PermissionDTO {
  permission: string;
  value: boolean;
}

export interface PermissionInfoDTO {
  permission: string;
  info: string;
}

export interface PermissionInfoGroupDTO {
  name: string;
  permissions: PermissionInfoDTO[];
}

export interface PermissionGroupDTO {
  name: string;
  formattedName: string;
  permissions: PermissionDTO[];
}

interface UserPermissionsDO {
  id: string;
  permissions: PermissionGroupDTO[];
}

export interface UserPermissionsDTO extends ProgressDO, ResponseErrorDO {
  data: {
    details: UserPermissionsDO;
  };
}
